@if (isSearchOpen()) {
  <button
    type="button"
    class="fixed inset-0 z-[100] cursor-default bg-neutral/55"
    [attr.aria-label]="'Close search' | translate"
    (click)="closeSearch()"
  ></button>
}

<div
  data-search-surface
  class="relative z-[110] hidden grow sm:block sm:max-w-[661px] sm:mx-6 lg:ms-12"
>
  <form
    class="flex h-11 rounded-[6px] border border-base-300 bg-base-100 shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20"
    role="search"
    (ngSubmit)="refreshProducts()"
  >
    <input
      data-search-keyword
      type="search"
      class="grow min-w-0 bg-transparent px-4 text-base outline-none"
      [formControl]="keywordControl"
      [placeholder]="'Search' | translate"
      [attr.aria-label]="'Search' | translate"
      (focus)="openSearch()"
    />

    <div class="relative h-full border-s border-base-300">
      <button
        data-category-trigger
        type="button"
        class="flex h-full w-40 items-center justify-between gap-2 px-3 text-sm text-base-content/70"
        [attr.aria-expanded]="isCategoryOpen()"
        aria-controls="desktop-category-options"
        aria-haspopup="listbox"
        (click)="toggleCategory()"
      >
        <span class="truncate">{{ selectedCategoryLabel() | translate }}</span>
        <sham-i-arrow-down-01-Stroke
          class="shrink-0 transition-transform"
          [class.rotate-180]="isCategoryOpen()"
        />
      </button>

      @if (isCategoryOpen()) {
        <div
          class="absolute end-0 top-[calc(100%+0.25rem)] z-50 w-80 max-w-[calc(100vw-1rem)] rounded-box border border-base-300 bg-base-100 p-2 shadow-xl"
        >
          <ng-container
            [ngTemplateOutlet]="categoryOptions"
            [ngTemplateOutletContext]="{ listId: 'desktop-category-options' }"
          />
        </div>
      }
    </div>

    <button
      type="submit"
      class="btn btn-primary h-full w-16 rounded-none rounded-e-[5px] border-0"
      [attr.aria-label]="'Search' | translate"
    >
      <sham-i-search />
    </button>
  </form>

  @if (isSearchOpen()) {
    <section
      id="desktop-search-results"
      class="absolute inset-x-0 top-[calc(100%+0.5rem)] z-40 overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-2xl"
      [attr.aria-label]="'Search results' | translate"
    >
      <ng-container [ngTemplateOutlet]="searchResults" />
    </section>
  }
</div>

<button
  data-mobile-search-trigger
  type="button"
  class="btn btn-ghost btn-square relative z-[110] sm:hidden"
  [attr.aria-label]="'Search' | translate"
  [attr.aria-expanded]="isSearchOpen()"
  aria-controls="mobile-search-surface"
  (click)="toggleMobileSearch()"
>
  <sham-i-search class="*:size-6" />
</button>

@if (isSearchOpen()) {
  <div
    id="mobile-search-surface"
    data-search-surface
    class="absolute inset-x-2 top-[calc(100%+0.5rem)] z-[110] rounded-box border border-base-300 bg-base-100 shadow-2xl sm:hidden"
  >
    <form
      class="flex h-11 border-b border-base-300"
      role="search"
      (ngSubmit)="refreshProducts()"
    >
      <input
        data-search-keyword
        type="search"
        class="grow min-w-0 bg-transparent px-4 text-base outline-none"
        [formControl]="keywordControl"
        [placeholder]="'Search' | translate"
        [attr.aria-label]="'Search' | translate"
      />
      <button
        type="submit"
        class="btn btn-primary h-full w-14 rounded-none rounded-se-box border-0"
        [attr.aria-label]="'Search' | translate"
      >
        <sham-i-search />
      </button>
    </form>

    <div class="relative border-b border-base-300 p-2">
      <button
        data-category-trigger
        type="button"
        class="btn btn-ghost btn-sm w-full justify-between"
        [attr.aria-expanded]="isCategoryOpen()"
        aria-controls="mobile-category-options"
        aria-haspopup="listbox"
        (click)="toggleCategory()"
      >
        <span class="truncate">{{ selectedCategoryLabel() | translate }}</span>
        <sham-i-arrow-down-01-Stroke [class.rotate-180]="isCategoryOpen()" />
      </button>

      @if (isCategoryOpen()) {
        <div
          class="absolute inset-x-2 top-full z-50 rounded-box border border-base-300 bg-base-100 p-2 shadow-xl"
        >
          <ng-container
            [ngTemplateOutlet]="categoryOptions"
            [ngTemplateOutletContext]="{ listId: 'mobile-category-options' }"
          />
        </div>
      }
    </div>

    <section [attr.aria-label]="'Search results' | translate">
      <ng-container [ngTemplateOutlet]="searchResults" />
    </section>
  </div>
}

<ng-template #categoryOptions let-listId="listId">
  <label class="sr-only" [for]="listId + '-search'">
    {{ 'Search categories' | translate }}
  </label>
  <input
    [id]="listId + '-search'"
    type="search"
    class="input input-sm w-full"
    [formControl]="categoryQueryControl"
    [placeholder]="'Search categories' | translate"
    role="combobox"
    autocomplete="off"
    [attr.aria-controls]="listId"
    [attr.aria-expanded]="isCategoryOpen()"
    [attr.aria-activedescendant]="
      categories().length ? listId + '-option-' + activeCategoryIndex() : null
    "
    aria-autocomplete="list"
    (keydown)="onCategoryInputKeydown($event)"
  />

  <div [id]="listId" class="mt-2 max-h-64 overflow-y-auto" role="listbox">
    <button
      type="button"
      class="btn btn-ghost btn-sm mb-1 w-full justify-start"
      role="option"
      [attr.aria-selected]="selectedCategory() === null"
      (click)="clearCategory()"
    >
      {{ 'All' | translate }}
    </button>

    @if (categories().length) {
      <ul class="list gap-1" role="presentation">
        @for (category of categories(); track category.id; let index = $index) {
          <li role="presentation">
            <button
              [id]="listId + '-option-' + index"
              type="button"
              class="flex w-full cursor-pointer rounded-field px-3 py-2 text-start hover:bg-base-200 focus-visible:outline-2 focus-visible:outline-primary"
              [class.bg-base-200]="activeCategoryIndex() === index"
              [class.ps-6]="category.level === 1"
              [class.ps-10]="category.level > 1"
              role="option"
              [attr.aria-selected]="selectedCategory()?.id === category.id"
              (mouseenter)="activeCategoryIndex.set(index)"
              (click)="selectCategory(category)"
            >
              <span class="min-w-0 grow">
                <span
                  class="block truncate"
                  [class.font-semibold]="category.level === 0"
                  [class.font-medium]="category.level > 0"
                >
                  {{ category.name }}
                </span>
                @if (category.level > 0) {
                  <span class="block truncate text-xs text-base-content/60">
                    {{ category.breadcrumb }}
                  </span>
                }
              </span>
            </button>
          </li>
        }
      </ul>
    } @else {
      <p class="px-3 py-6 text-center text-sm text-base-content/60" role="status">
        {{ 'No categories found' | translate }}
      </p>
    }
  </div>
</ng-template>

<ng-template #searchResults>
  @if (isLoading()) {
    <div class="flex min-h-40 items-center justify-center gap-3" role="status">
      <span class="loading loading-spinner loading-sm"></span>
      <span>{{ 'Searching...' | translate }}</span>
    </div>
  } @else if (hasError()) {
    <div class="p-6 text-center" role="alert">
      <p class="text-error">
        {{ 'Products could not be loaded. Please try again.' | translate }}
      </p>
      <button type="button" class="btn btn-sm mt-4" (click)="retryProducts()">
        {{ 'Try again' | translate }}
      </button>
    </div>
  } @else if (products().length) {
    <ul class="list max-h-[min(28rem,60vh)] overflow-y-auto p-2">
      @for (product of products(); track product.id) {
        <li>
          <button
            type="button"
            class="list-row w-full cursor-pointer rounded-box p-3 text-start hover:bg-base-200 focus-visible:outline-2 focus-visible:outline-primary"
            (click)="openProduct(product)"
          >
            <img
              class="size-16 rounded-field bg-base-200 object-cover"
              [src]="productImage(product)"
              [alt]="product.name"
            />
            <span class="list-col-grow min-w-0">
              <span class="block truncate font-semibold">{{ product.name }}</span>
              <span class="block truncate text-xs text-base-content/60">
                {{ product.categoryName }}
              </span>
              <span class="mt-1 block text-sm font-bold text-primary">
                @if (productCurrency(product) === 'USD') {
                  SYP&nbsp;{{ productPrice(product) | number: '1.0-2' }}
                } @else {
                  {{ productCurrency(product) }}&nbsp;{{
                    productPrice(product) | number: '1.0-2'
                  }}
                }
              </span>
            </span>
          </button>
        </li>
      }
    </ul>
    @if (canShowMore()) {
      <div class="p-4 text-center">
        <button type="button" class="btn btn-primary btn-sm" (click)="showMore()">
          {{ 'Show More' | translate }}
        </button>
      </div>
    }
  } @else {
    <div class="p-6 text-center" aria-live="polite">
      <p class="font-medium">{{ 'No items found' | translate }}</p>
    </div>
  }
</ng-template>
